<template>
  <div class="px-6 py-4" style="max-height: 80vh; overflow-y: auto;">
    <!-- Client Info Header -->
    <div class="mb-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-dark-700 dark:to-dark-600 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-2 text-sm">
        <div v-if="clientDisplayName">
          <span class="text-gray-600 dark:text-dark-400">Name:</span>
          <span class="ml-2 font-medium text-gray-900 dark:text-dark-100">{{ clientDisplayName }}</span>
        </div>
        <div v-if="clientCompanyName">
          <span class="text-gray-600 dark:text-dark-400">Company Name:</span>
          <span class="ml-2 font-medium text-gray-900 dark:text-dark-100">{{ clientCompanyName }}</span>
        </div>
        <div v-if="clientPhone">
          <span class="text-gray-600 dark:text-dark-400">Phone:</span>
          <span class="ml-2 font-medium text-gray-900 dark:text-dark-100">{{ clientPhone }}</span>
        </div>
        <div v-if="clientEmail">
          <span class="text-gray-600 dark:text-dark-400">Email:</span>
          <span class="ml-2 font-medium text-gray-900 dark:text-dark-100">{{ clientEmail }}</span>
        </div>
        <div>
          <span class="text-gray-600 dark:text-dark-400">Pending Orders:</span>
          <span class="ml-2 font-semibold text-orange-600 dark:text-orange-400">{{ client.pendingOrders }}</span>
        </div>
        <div>
          <span class="text-gray-600 dark:text-dark-400">Total Outstanding:</span>
          <span class="ml-2 font-semibold text-red-600 dark:text-red-400">₱{{ formatPrice(client.outstanding) }}</span>
        </div>
      </div>
    </div>

    <!-- Filters Section -->
    <div class="mb-4 bg-gray-50 dark:bg-dark-700 p-3 rounded-lg border border-gray-200 dark:border-dark-600">
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <!-- Sort By -->
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-dark-200 mb-1">Sort By</label>
          <select
            v-model="filters.sort"
            class="w-full rounded-md border-gray-300 dark:border-dark-500 bg-white dark:bg-dark-800 text-gray-900 dark:text-dark-100 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
            @change="fetchOrders"
          >
            <option value="oldest">Oldest First</option>
            <option value="latest">Latest First</option>
            <option value="amount_high">Amount (High to Low)</option>
            <option value="amount_low">Amount (Low to High)</option>
          </select>
        </div>
        
        <!-- Status Filter -->
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-dark-200 mb-1">Order Status</label>
          <select
            v-model="filters.status"
            class="w-full rounded-md border-gray-300 dark:border-dark-500 bg-white dark:bg-dark-800 text-gray-900 dark:text-dark-100 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
            @change="fetchOrders"
          >
            <option value="pending_payment">Pending Payment (Default)</option>
            <option value="all">All Orders</option>
            <option value="pending">Pending</option>
            <option value="processing">Processing</option>
            <option value="ready_release">Ready for Release</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
      </div>
    </div>

    <!-- Results Table -->
    <div class="overflow-x-auto border border-gray-200 dark:border-dark-600 rounded-lg shadow-sm mb-4">
      <table class="min-w-full divide-y divide-gray-200 dark:divide-dark-600">
        <thead class="bg-gray-50 dark:bg-dark-700">
          <tr>
            <th scope="col" class="px-6 py-2 text-left text-xs font-medium text-gray-500 dark:text-dark-400 uppercase tracking-wider">Order #</th>
            <th scope="col" class="px-6 py-2 text-left text-xs font-medium text-gray-500 dark:text-dark-400 uppercase tracking-wider">Date</th>
            <th scope="col" class="px-6 py-2 text-left text-xs font-medium text-gray-500 dark:text-dark-400 uppercase tracking-wider">Status</th>
            <th scope="col" class="px-6 py-2 text-right text-xs font-medium text-gray-500 dark:text-dark-400 uppercase tracking-wider">Total</th>
            <th scope="col" class="px-6 py-2 text-right text-xs font-medium text-gray-500 dark:text-dark-400 uppercase tracking-wider">Paid</th>
            <th scope="col" class="px-6 py-2 text-right text-xs font-medium text-gray-500 dark:text-dark-400 uppercase tracking-wider">Balance</th>
            <th scope="col" class="px-6 py-2 text-center text-xs font-medium text-gray-500 dark:text-dark-400 uppercase tracking-wider">Actions</th>
          </tr>
        </thead>
        <tbody class="bg-white dark:bg-dark-800 divide-y divide-gray-200 dark:divide-dark-600">
          <template v-if="loading">
            <tr>
              <td colspan="7" class="px-6 py-3 text-center text-sm text-gray-500 dark:text-dark-400">Loading orders...</td>
            </tr>
          </template>
          <template v-else-if="orders.length === 0">
            <tr>
              <td colspan="7" class="px-6 py-3 text-center text-sm text-gray-500 dark:text-dark-400">
                {{ filters.status === 'pending_payment' ? 'No orders with pending payments found' : 'No orders found for this client' }}
              </td>
            </tr>
          </template>
          <template v-else>
            <tr v-for="order in orders" :key="order.id" class="hover:bg-gray-50 dark:hover:bg-dark-700">
              <td class="px-6 py-2 whitespace-nowrap">
                <div class="text-sm font-medium text-gray-900 dark:text-dark-100">#{{ order.order_number }}</div>
              </td>
              <td class="px-6 py-2 whitespace-nowrap">
                <div class="text-sm text-gray-500 dark:text-dark-400">{{ formatDate(order.created_at) }}</div>
              </td>
              <td class="px-6 py-2 whitespace-nowrap">
                <span class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full"
                      :class="getStatusClass(order.status)">
                  {{ formatStatus(order.status) }}
                </span>
              </td>
              <td class="px-6 py-2 whitespace-nowrap text-right">
                <div class="text-sm text-gray-900 dark:text-dark-100">₱{{ formatPrice(order.total_amount) }}</div>
              </td>
              <td class="px-6 py-2 whitespace-nowrap text-right">
                <div class="text-sm text-green-600 dark:text-green-400">₱{{ formatPrice(order.amount_paid) }}</div>
              </td>
              <td class="px-6 py-2 whitespace-nowrap text-right">
                <div class="text-sm font-medium" :class="order.balance > 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'">
                  ₱{{ formatPrice(order.balance) }}
                </div>
              </td>
              <td class="px-6 py-2 whitespace-nowrap text-center text-sm">
                <button 
                  @click="viewOrder(order)" 
                  class="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                  title="View Order"
                >
                  <i class="fas fa-eye"></i>
                </button>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>

    <!-- Summary -->
    <div class="bg-gray-50 dark:bg-dark-700 p-4 rounded-lg border border-gray-200 dark:border-dark-600">
      <div class="grid grid-cols-3 gap-4 text-center">
        <div>
          <div class="text-sm text-gray-600 dark:text-dark-400">Total Orders</div>
          <div class="text-2xl font-bold text-gray-900 dark:text-dark-100">{{ orders.length }}</div>
        </div>
        <div>
          <div class="text-sm text-gray-600 dark:text-dark-400">Total Amount</div>
          <div class="text-2xl font-bold text-gray-900 dark:text-dark-100">₱{{ formatPrice(totalAmount) }}</div>
        </div>
        <div>
          <div class="text-sm text-gray-600 dark:text-dark-400">Total Balance</div>
          <div class="text-2xl font-bold text-red-600 dark:text-red-400">₱{{ formatPrice(totalBalance) }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { useApi } from '@/composables/useApi';
import { useToast } from '@/composables/useToast';

const api = useApi();
const toast = useToast();

// Props
const props = defineProps({
  client: {
    type: Object,
    required: true
  }
});

// Emit events
const emit = defineEmits(['view-order']);

// State
const orders = ref([]);
const loading = ref(false);
const filters = ref({
  sort: 'oldest',
  status: 'pending_payment' // Only show orders with pending payments by default
});

// Computed properties
const totalAmount = computed(() => {
  return orders.value.reduce((sum, order) => sum + parseFloat(order.total_amount || 0), 0);
});

const totalBalance = computed(() => {
  return orders.value.reduce((sum, order) => sum + parseFloat(order.balance || 0), 0);
});

const clientDisplayName = computed(() => {
  return props.client.display_name || props.client.name || '';
});

const clientCompanyName = computed(() => {
  return props.client.company_name || props.client.company || '';
});

const clientPhone = computed(() => {
  return props.client.phone || props.client.contact_number || props.client.phone_number || '';
});

const clientEmail = computed(() => {
  return props.client.email || props.client.contact_email || '';
});

// Methods
const fetchOrders = async () => {
  try {
    loading.value = true;
    console.log('Fetching orders for client:', props.client.id);
    
    const params = {
      client_id: props.client.id,
      sort: filters.value.sort,
      include_balance: true
    };
    
    // Handle different status filters
    if (filters.value.status === 'pending_payment') {
      // Don't send status param to backend, we'll filter client-side
      // This allows us to filter by balance
    } else if (filters.value.status !== 'all') {
      params.status = filters.value.status;
    }
    
    const response = await api.get('/orders', { params });
    
    if (response.data) {
      console.log('Orders response:', response.data);
      
      // Handle both array and paginated responses
      let orderList = Array.isArray(response.data) ? response.data : (response.data.data || []);
      
      // Calculate balance for each order
      orderList = orderList.map(order => ({
        ...order,
        amount_paid: parseFloat(order.amount_paid || 0),
        total_amount: parseFloat(order.total_amount || 0),
        balance: parseFloat(order.total_amount || 0) - parseFloat(order.amount_paid || 0)
      }));
      
      // Filter for pending payment orders (exclude completed, rejected, cancelled, inactive and fully paid)
      if (filters.value.status === 'pending_payment') {
        orderList = orderList.filter(order => {
          const hasBalance = order.balance > 0;
          const isNotCompleted = !['completed', 'rejected', 'cancelled'].includes(order.status);
          const isActive = order.is_active !== false; // Include if is_active is true or null/undefined
          return hasBalance && isNotCompleted && isActive;
        });
      }
      
      orders.value = orderList;
      
      // Apply sorting
      applySort();
    }
  } catch (error) {
    console.error('Error fetching orders:', error);
    toast.error('Failed to load orders');
  } finally {
    loading.value = false;
  }
};

const applySort = () => {
  orders.value.sort((a, b) => {
    switch (filters.value.sort) {
      case 'oldest':
        return new Date(a.created_at) - new Date(b.created_at);
      case 'latest':
        return new Date(b.created_at) - new Date(a.created_at);
      case 'amount_high':
        return b.total_amount - a.total_amount;
      case 'amount_low':
        return a.total_amount - b.total_amount;
      default:
        return 0;
    }
  });
};

const viewOrder = (order) => {
  emit('view-order', order.id);
};

const formatDate = (dateValue) => {
  if (!dateValue) return 'N/A';
  
  try {
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) return 'N/A';
    
    return date.toLocaleDateString('en-PH', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  } catch (error) {
    return 'N/A';
  }
};

const formatPrice = (price) => {
  return Number(price || 0).toLocaleString('en-PH', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

const formatStatus = (status) => {
  if (!status) return 'N/A';
  return status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

const getStatusClass = (status) => {
  const statusClasses = {
    'pending': 'bg-yellow-100 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-400',
    'processing': 'bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-400',
    'ready_release': 'bg-purple-100 dark:bg-purple-900/20 text-purple-800 dark:text-purple-400',
    'completed': 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-400',
    'cancelled': 'bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-400',
    'rejected': 'bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-400'
  };
  return statusClasses[status] || 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300';
};

// Setup
onMounted(() => {
  fetchOrders();
});
</script>

